import React, { useEffect, useState, useRef } from "react";
import { Send, MessageCircle, Clock, Loader2 } from "lucide-react";
import { api, asItems, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Conversation thread panel for a property.
 * Visible to an authorized listing owner, assigned agent, office, or administrator.
 */
export default function PropertyThreadPanel({ propertyId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [allowed, setAllowed] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef(null);

  // Public property responses deliberately omit owner/assignment ids. Allow
  // only eligible roles to attempt loading; the backend remains authoritative and
  // applies its per-property owner/agent/office authorization check.
  useEffect(() => {
    if (!user) { setAllowed(false); setLoading(false); return; }
    const role = String(user.role || "").toLowerCase().replace("office_user", "office");
    const eligibleRole = ["admin", "super_admin", "office", "agent", "owner", "buyer"].includes(role);
    setAllowed(eligibleRole);
    if (!eligibleRole) setLoading(false);
  }, [user]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/properties/${propertyId}/threads`);
      setItems(asItems(data));
    } catch (e) {
      if (e?.response?.status === 403 || e?.response?.status === 401) {
        setAllowed(false);
      } else {
        setError(formatApiError(e));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allowed && propertyId) load();
    // eslint-disable-next-line
  }, [allowed, propertyId]);

  useEffect(() => {
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    endRef.current?.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth" });
  }, [items.length]);

  if (!user || !allowed) return null;

  const send = async () => {
    if (!msg.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/threads`, { message: msg.trim() });
      setItems((prev) => [...prev, data]);
      setMsg("");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setBusy(false);
    }
  };

  const roleBadge = (r) => ({
    admin: "bg-primary text-primary-foreground",
    super_admin: "bg-primary text-primary-foreground",
    office: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]",
    agent: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]",
    owner: "bg-blue-500/10 text-blue-700 dark:text-blue-300",
    buyer: "bg-muted text-muted-foreground",
  }[r] || "bg-muted");

  return (
    <section className="border border-border rounded-lg bg-background overflow-hidden" data-testid="property-thread-panel">
      <header className="px-5 py-4 border-b border-border bg-secondary/30 flex items-center gap-2">
        <MessageCircle className="w-4 h-4 text-primary" />
        <h3 className="font-display font-bold text-primary">Moderation Conversation</h3>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-muted-foreground">
          Owner · Agent · Office · Admin
        </span>
      </header>

      <div className="max-h-[420px] overflow-y-auto px-5 py-4 space-y-4" data-testid="thread-messages">
        {loading && <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-primary" /> Loading conversation…</p>}
        {!loading && error && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            <p>{error}</p>
            <Button type="button" variant="outline" size="sm" className="mt-3" onClick={load}>Retry</Button>
          </div>
        )}
        {!loading && !error && items.length === 0 && (
          <div className="text-center py-8 text-sm text-muted-foreground">
            No messages yet. Start the conversation below.
          </div>
        )}
        {items.map((m) => {
          const mine = String(m.author_id || "") === String(user.user_id || user.id || "");
          return (
            <div
              key={m.thread_id || m.at}
              className={`flex flex-col gap-1 ${mine ? "items-end" : "items-start"}`}
              data-testid={`thread-msg-${m.author_role}`}
            >
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${roleBadge(m.author_role)}`}>
                  {m.author_role}
                </span>
                <span className="font-semibold text-primary">{m.author_name}</span>
                <Clock className="w-3 h-3" />
                <span>{new Date(m.at).toLocaleString("en-IN")}</span>
              </div>
              <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm leading-relaxed ${
                mine ? "bg-primary text-primary-foreground" : "bg-secondary text-primary"
              }`}>
                {m.message}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <div className="border-t border-border p-3 flex items-end gap-2">
        <textarea
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={2}
          maxLength={2000}
          aria-label="Moderation message"
          placeholder="Write a message — Enter to send, Shift+Enter for new line"
          className="flex-1 resize-none border border-border rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:border-primary"
          data-testid="thread-input"
        />
        <Button type="button" onClick={send} disabled={busy || !msg.trim()} title="Send moderation message" className="font-bold gap-2" data-testid="thread-send">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
        </Button>
      </div>
    </section>
  );
}
